import { Route, BrowserRouter, Routes, Navigate } from 'react-router-dom';
import Login from './component/auth/Login';
import ForgotPassword from './component/auth/ForgotPassword';
import ResetPassword from './component/auth/ResetPassword';
import SetPassword from './component/auth/SetPassword';
import Layout from './component/layout';
import PrivateRoute from './component/auth/PrivateRoute';
import UserList from './component/user/UserList';
import AddUser from './component/user/AddUser';
import UserProfile from './component/user/UserProfile';
import ChangePassword from './component/user/ChangePassword';

import './assets/css/bootstrap.min.css';
import './assets/css/icons.min.css';
import './assets/css/app.min.css';
import './assets/css/style.css';
import './assets/css/calendar.css';
import './assets/css/custom-media-queries.css';
import './assets/css/custom-style.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { Suspense, useState, useEffect } from 'react'
import { AbacProvider } from "react-abac";
import { rules } from "./abac.config";

import projectServices from './component/project/services/projectServices';
import { setLoginUserData } from './component/project/redux/projectAction';
import { useSelector, useDispatch } from 'react-redux';
import Loader from './component/common/Loader';
import AccessDenied from './component/common/AccessDenied';
import PageNotFound from './component/common/PageNotFound';
import {
  canAccessEstimation,
  denyMaintenance,
  denyChartAndListing,
  canAccessHumanResource,
  canAccessRentalEquipment,
  denyShopPersonal,
  denyReceiving,
  denyTransport,
  denyInventory,
  denyFieldCrew,
  canAccessMasterData,
  hasPermission
} from './component/layout/utils/permissionUtils';
import { Permission } from './abac.config';

const withAccessCheck = (checkFn, isDenyCheck = false) => ({ element }) => {
  const loginUserData = useSelector(state => state.loginUserData);
  const roleKey = loginUserData?.roleKey || localStorage.getItem("roleKey") || "user";
  const result = checkFn(roleKey);
  const hasAccess = isDenyCheck ? !result : result;
  if (!hasAccess) return <AccessDenied />;
  return element;
};

const DashboardAndProjectRoute = ({ element }) => {
  const loginUserData = useSelector(state => state.loginUserData);
  const roleKey = loginUserData?.roleKey || localStorage.getItem("roleKey") || "user";
  if (roleKey === "user") return <AccessDenied />;
  return element;
};

const ArchiveAndTimeOffRoute = ({ element }) => {
  const loginUserData = useSelector(state => state.loginUserData);
  const roleKey = loginUserData?.roleKey || localStorage.getItem("roleKey") || "user";
  if (roleKey !== "superadmin" && roleKey !== "admin" && roleKey !== "projectmanager") {
    return <AccessDenied />;
  }
  return element;
};

const UserManagementRoute = ({ element }) => {
  const loginUserData = useSelector(state => state.loginUserData);
  const roleKey = loginUserData?.roleKey || localStorage.getItem("roleKey") || "user";
  if (!hasPermission(roleKey, Permission.USER_WRITE)) return <AccessDenied />;
  return element;
};

const ChartAndListingRoute = withAccessCheck(denyChartAndListing, true);
const EstimationRoute = withAccessCheck(canAccessEstimation);
const MaintenanceRoute = withAccessCheck(denyMaintenance, true);
const HumanResourceRoute = withAccessCheck(canAccessHumanResource);
const RentalEquipmentRoute = withAccessCheck(canAccessRentalEquipment);
const ShopPersonalRoute = withAccessCheck(denyShopPersonal, true);
const ReceivingRoute = withAccessCheck(denyReceiving, true);
const TransportRoute = withAccessCheck(denyTransport, true);
const InventoryRoute = withAccessCheck(denyInventory, true);
const FieldCrewRoute = withAccessCheck(denyFieldCrew, true);
const MasterDataRoute = withAccessCheck(canAccessMasterData);


// LAZY LOADED COMPONENTS - Project Related
const AddProject = React.lazy(() => import('./component/project/AddProject'));
const AddEstimation = React.lazy(() => import('./component/project/AddEstimation'));
const ViewEstimation = React.lazy(() => import('./component/project/ViewEstimation'));
const AddGcBids = React.lazy(() => import('./component/project/AddGCBids.js'));
const EstimationAddEditRevisionHistory = React.lazy(() => import('./component/project/AddEstimationRevision.js'));
const EditProject = React.lazy(() => import('./component/project/EditProject'));
const ProjectDocuments = React.lazy(() => import('./component/project/component/ProjectDocuments'));
const ProjectView = React.lazy(() => import('./component/project/component/ProjectView'));
const ProjectDocUpload = React.lazy(() => import('./component/project/component/ProjectDocUpload'));
const Transport = React.lazy(() => import('./component/calendar/component/Transport'));
const FieldCrewCalendar = React.lazy(() => import('./component/calendar/component/FieldCrewCalendar'));
const UpdateRecievingDetails = React.lazy(() => import('./component/project/component/UpdateRecievingDetails'));

// LAZY LOADED COMPONENTS - Vendor, QC, Location, Table, Shop, etc.
const VendorList = React.lazy(() => import('./component/vendor/VendorList'));
const AddVendor = React.lazy(() => import('./component/vendor/AddVendor'));
const QcList = React.lazy(() => import('./component/qc/QcList'));
const AddQc = React.lazy(() => import('./component/qc/AddQc'));
const LocationList = React.lazy(() => import('./component/location/LocationList'));
const AddLocation = React.lazy(() => import('./component/location/AddLocation'));
const TableList = React.lazy(() => import('./component/table/TableList'));
const AddTable = React.lazy(() => import('./component/table/AddTable'));
const ShopList = React.lazy(() => import('./component/shop/ShopList'));
const AddShop = React.lazy(() => import('./component/shop/AddShop'));
const DrawingTypeList = React.lazy(() => import('./component/drawingType/DrawingTypeList'));
const AddDrawingType = React.lazy(() => import('./component/drawingType/AddDrawingType'));
const FieldCrewList = React.lazy(() => import('./component/fieldCrew/FieldCrewList'));
const AddFieldCrew = React.lazy(() => import('./component/fieldCrew/AddFieldCrew'));
const GradeList = React.lazy(() => import('./component/GradeMasterData/GradeList'));
const AddGrade = React.lazy(() => import('./component/GradeMasterData/AddGrade'));

// LAZY LOADED COMPONENTS - Maintenance
const Maintenance = React.lazy(() => import('./component/project/component/Maintenance/Maintenance'));
const ShopMasterDataMaintenance = React.lazy(() => import('./component/project/component/Maintenance/Shop/ShopMasterDataMaintenance'));
const ShopMaintenanceHistory = React.lazy(() => import('./component/project/component/Maintenance/Shop/ShopMaintenanceHistory'));
const ShopMaintenanceHistoryDetail = React.lazy(() => import('./component/project/component/Maintenance/Shop/ShopMaintenanceHistoryDetail'));
const FieldMasterDataMaintenance = React.lazy(() => import('./component/project/component/Maintenance/Field/FieldMasterDataMaintenance'));
const FieldMaintenanceHistory = React.lazy(() => import('./component/project/component/Maintenance/Field/FieldmaintenanceHistory'));
const FieldMaintenanceHistoryDetails = React.lazy(() => import('./component/project/component/Maintenance/Field/FieldMaintenanceHistoryDetails'));
const ShopMaintenanceList = React.lazy(() => import('./component/ShopMaintenance/ShopMaintenanceList'));
const AddShopMaintenance = React.lazy(() => import('./component/ShopMaintenance/AddShopMaintenance'));
const FieldMaintenanceList = React.lazy(() => import('./component/FieldMaintenance/FieldMaintenanceList'));
const AddFieldMaintenance = React.lazy(() => import('./component/FieldMaintenance/AddFieldMaintenance'));
const FieldMaintenancelist = React.lazy(() => import('./component/FieldMaintenanceTypes/fieldMaintenancelist'));
const AddFieldMaintenanceitem = React.lazy(() => import('./component/FieldMaintenanceTypes/AddFieldMaintenanceitem'));

// LAZY LOADED COMPONENTS - Human Resource
const HumanResource = React.lazy(() => import('./component/HumanResourcePage/HumanResource'));
const AddEmployee = React.lazy(() => import('./component/HumanResourcePage/AddEmployee'));

// LAZY LOADED COMPONENTS - Certificates
const CertificateList = React.lazy(() => import('./component/CertificationCheckList/CertificateList'));
const AddCertificate = React.lazy(() => import('./component/CertificationCheckList/AddCertificate'));

// LAZY LOADED COMPONENTS - Inventory, Designations, Titles
const InventoryView = React.lazy(() => import('./component/project/component/Inventory/InventoryView'));
const ShopDesignationList = React.lazy(() => import('./component/ShopDesignation/ShopDesignationList'));
const AddShopDesignation = React.lazy(() => import('./component/ShopDesignation/AddShopDesignation'));
const TitleList = React.lazy(() => import('./component/Title/TitleList'));
const AddTitle = React.lazy(() => import('./component/Title/AddTitle'));

// LAZY LOADED COMPONENTS - Employee Contact
const EmployeeContact = React.lazy(() => import('./component/EmployeeContact/EmployeeContact'));
const AddGroup = React.lazy(() => import('./component/EmployeeContact/AddGroup'));
const AllGroupList = React.lazy(() => import('./component/EmployeeContact/AllGroupList'));
const SendMail = React.lazy(() => import('./component/EmployeeContact/SendMail'));
const TemplateLists = React.lazy(() => import('./component/EmployeeContact/TemplateLists'));
const AddTemplate = React.lazy(() => import('./component/EmployeeContact/AddTemplate'));

// Already Lazy Loaded Components
const RestoreUser = React.lazy(() => import('./component/user/RestoreUser'));
const ProjectListingPage = React.lazy(() => import('./component/calendar/component/ProjectListingPage'));

const CreateProject = React.lazy(() => import('./component/project/component/CreateProject'));
const ScheduleCalendar = React.lazy(() => import('./component/calendar/component/ScheduleCalendar'));
const Project = React.lazy(() => import('./component/project/Projects'));
const Dashboard = React.lazy(() => import('./component/project/component/Dashboard/Dashboard.jsx'));
const ShopPersonal = React.lazy(() => import('./component/calendar/component/ShopPersonal'));
const Receiving = React.lazy(() => import('./component/project/component/Receiving'));
const ProjectChart = React.lazy(() => import('./component/project/component/ProjectChartView'));
const ProjectListing = React.lazy(() => import('./component/project/ProjectListing.js'));
const Estimation = React.lazy(() => import('./component/project/Estimation.js'));
const ColorCodeList = React.lazy(() => import('./component/PMColorMasterData/ColorCodeList'));
const AddColorCode = React.lazy(() => import('./component/PMColorMasterData/AddColorCode'));
const EquipmentList = React.lazy(() => import('./component/EquipmentTypeMasterData/EquipmentList.jsx'));
const AddEquipment = React.lazy(() => import('./component/EquipmentTypeMasterData/AddEquipment.jsx'));
const RentalEquipment = React.lazy(() => import('./component/RentalEquipment/RentalEquipment.jsx'));
const ArchievedProjects = React.lazy(() => import('./component/project/ArchivedProjects.js'));
const ProjectTypeList = React.lazy(() => import('./component/ProjectTypeMasterData/ProjectTypeList.jsx'));
const AddProjectType = React.lazy(() => import('./component/ProjectTypeMasterData/AddProjectType.jsx'));
const GeneralContractorList = React.lazy(() => import('./component/calendar/component/GeneralContractor/GeneralContractorList.jsx'));
const AddGeneralContractor = React.lazy(() => import('./component/calendar/component/GeneralContractor/AddGeneralContractor.jsx'));

function App() {
  const [roleKey, setRoleKey] = useState(localStorage.getItem("roleKey") || "user");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const loginUserData = useSelector(state => state.loginUserData);
  const dispatch = useDispatch();

  const getLoginUserData = () => {
    let loginUserDataClone = { ...loginUserData };
    try {
      projectServices.getUserDetailByToken().then((res) => {
        if (res.data.status) {
          let result = res.data.data;
          const profileData = result?.profile || result;
          setIsLoggedIn(true);
          dispatch(setLoginUserData({
            ...loginUserDataClone,
            userName: profileData?.name || "",
            profileImage: profileData?.profileImage || "",
            roleKey: profileData?.roleKey || ""
          }));
          setRoleKey(profileData?.roleKey || "");
        }
      }).catch((error) => {
        console.log(error);
        setIsLoggedIn(false);
        if (error.response && error.response.status === 401) {
            const existingLogoutMessage = sessionStorage.getItem('logoutMessage');
            localStorage.clear();
            if (existingLogoutMessage) {
                sessionStorage.setItem('logoutMessage', existingLogoutMessage);
            }
            const path = window.location.pathname;
            const isPublicRoute = path.startsWith('/login') || 
                                  path.startsWith('/forgotpassword') || 
                                  path.startsWith('/resetpassword') || 
                                  path.startsWith('/setpassword');
            
            if (!isPublicRoute) {
                window.location.href = '/login';
            }
        }
      })
    } catch (error) {
      console.log({ error })
    }
  };
  
  useEffect(() => {
    getLoginUserData();
    
    const handleStorageChange = (e) => {
      // If user logs out in another tab
      if (e.key === 'user-token' && !e.newValue) {
        window.location.href = '/login';
      }
      // If a different user logs in another tab
      if (e.key === 'userId' && e.newValue && e.oldValue !== e.newValue) {
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
    // eslint-disable-next-line
  }, []);

  const updateRoleKey = (loginRoleKey) => {
    setRoleKey(loginRoleKey);
  };

  let user = {
    "id": 2,
    "name": "user",
    "roles": ["user"],
    "permissions": []
  };
  if (roleKey !== "") {
    user = {
      "id": localStorage.getItem("userId", "1"),
      "name": localStorage.getItem("userName", "user"),
      "roles": [roleKey],
      "permissions": []
    };
  }

  return (
    <div className="App">
      {roleKey !== "" &&
        <div>
          <ToastContainer pauseOnFocusLoss={false} />
          <AbacProvider
            rules={rules}
            user={user}
            roles={user.roles}
            permissions={user.permissions}
          >
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<Login updateRoleKey={updateRoleKey} isLoggedIn={isLoggedIn} />} />
                <Route path="/forgotpassword" element={<ForgotPassword />} />
                <Route path="/resetpassword/:resetKey" element={<ResetPassword />} />
                <Route path="/setpassword/:resetKey" element={<SetPassword />} />
                <Route path="/*" element={<PrivateRoute><Layout /></PrivateRoute>}>
                  <Route index element={
                    <Suspense fallback={<Loader />}>
                      {roleKey === 'user' ? <Navigate to="/transport" replace /> : <Dashboard />}
                    </Suspense>
                  } />
                  <Route path="dashboard" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<Dashboard />} /></Suspense>} />
                  <Route path="archive" element={<Suspense fallback={<Loader />}><ArchiveAndTimeOffRoute element={<ArchievedProjects />} /></Suspense>} />
                  <Route path="project" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<Project />} /></Suspense>} />
                  <Route path="project/edit/:projectId" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<EditProject />} /></Suspense>} />
                  <Route path="project/drawing/:projectId" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<CreateProject />} /></Suspense>} />
                  <Route path="project/fieldMeasure/:projectId" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<CreateProject />} /></Suspense>} />
                  <Route path="project/material/:projectId" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<CreateProject />} /></Suspense>} />
                  <Route path="project/shop-fabrication/:projectId" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<CreateProject />} /></Suspense>} />
                  <Route path="project/freight-delivery/:projectId" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<CreateProject />} /></Suspense>} />
                  <Route path="project/field-install/:projectId" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<CreateProject />} /></Suspense>} />
                  <Route path="project/project-document/:projectId" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<ProjectDocuments />} /></Suspense>} />
                  <Route path="project/project-view/:projectId" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<ProjectView />} /></Suspense>} />
                  <Route path="project/project-doc-upload/:projectId/:ref" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<ProjectDocUpload />} /></Suspense>} />
                  <Route path="timeOffcalendar" element={<Suspense fallback={<Loader />}><ArchiveAndTimeOffRoute element={<ScheduleCalendar />} /></Suspense>} />
                  <Route path="transport" element={<Suspense fallback={<Loader />}><TransportRoute element={<Transport />} /></Suspense>} />
                  <Route path="project/add" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<AddProject />} /></Suspense>} />
                  <Route path="estimation/add" element={<Suspense fallback={<Loader />}><EstimationRoute element={<AddEstimation />} /></Suspense>} />
                  <Route path="estimation/view/:projectId" element={<Suspense fallback={<Loader />}><EstimationRoute element={<ViewEstimation />} /></Suspense>} />
                  <Route path="estimation/gc-bids" element={<Suspense fallback={<Loader />}><EstimationRoute element={<AddGcBids />} /></Suspense>} />
                  <Route path="estimation/revision" element={<Suspense fallback={<Loader />}><EstimationRoute element={<EstimationAddEditRevisionHistory />} /></Suspense>} />
                  <Route path="chartsProject" element={<Suspense fallback={<Loader />}><ChartAndListingRoute element={<ProjectChart />} /></Suspense>} />
                  <Route path="listingProjects" element={<Suspense fallback={<Loader />}><ChartAndListingRoute element={<ProjectListing />} /></Suspense>} />
                  <Route path="estimation" element={<Suspense fallback={<Loader />}><EstimationRoute element={<Estimation />} /></Suspense>} />
                  <Route path="fieldcrewcalender" element={<Suspense fallback={<Loader />}><FieldCrewRoute element={<FieldCrewCalendar />} /></Suspense>} />
                  <Route path="user" element={<UserManagementRoute element={<UserList />} />} />
                  <Route path="adduser" element={<UserManagementRoute element={<AddUser />} />} />
                  <Route path="userprofile" element={<UserManagementRoute element={<UserProfile />} />} />
                  <Route path="changepassword" element={<UserManagementRoute element={<ChangePassword />} />} />
                  <Route path="restore/:emailId" element={<Suspense fallback={<Loader />}><UserManagementRoute element={<RestoreUser />} /></Suspense>} />
                  <Route path="update/:userId" element={<UserManagementRoute element={<AddUser />} />} />
                  <Route path="vendor" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<VendorList />} /></Suspense>} />
                  <Route path="addvendor" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddVendor />} /></Suspense>} />
                  <Route path="vendor/:vendorId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddVendor />} /></Suspense>} />
                  <Route path="qc" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<QcList />} /></Suspense>} />
                  <Route path="addqc" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddQc />} /></Suspense>} />
                  <Route path="qc/:qcId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddQc />} /></Suspense>} />
                  <Route path="location" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<LocationList />} /></Suspense>} />
                  <Route path="addlocation" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddLocation />} /></Suspense>} />
                  <Route path="location/:locationId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddLocation />} /></Suspense>} />
                  <Route path="table" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<TableList />} /></Suspense>} />
                  <Route path="addtable" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddTable />} /></Suspense>} />
                  <Route path="table/:tableId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddTable />} /></Suspense>} />
                  <Route path="shop" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<ShopList />} /></Suspense>} />
                  <Route path="addshop" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddShop />} /></Suspense>} />
                  <Route path="shop/:shopId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddShop />} /></Suspense>} />
                  <Route path="drawingtype" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<DrawingTypeList />} /></Suspense>} />
                  <Route path="adddrawingtype" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddDrawingType />} /></Suspense>} />
                  <Route path="drawingtype/:drawingTypeId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddDrawingType />} /></Suspense>} />
                  <Route path="fieldcrew" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<FieldCrewList />} /></Suspense>} />
                  <Route path="addfieldcrew" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddFieldCrew />} /></Suspense>} />
                  <Route path="fieldcrew/:fieldCrewId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddFieldCrew />} /></Suspense>} />
                  <Route path="ShopMaintenance" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<ShopMaintenanceList />} /></Suspense>} />
                  <Route path="AddShopMaintenance" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<AddShopMaintenance />} /></Suspense>} />
                  <Route path="ShopMaintenance/:ShopMaintenanceId" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<AddShopMaintenance />} /></Suspense>} />
                  <Route path="FieldMaintenance" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<FieldMaintenanceList />} /></Suspense>} />
                  <Route path="AddFieldMaintenance" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<AddFieldMaintenance />} /></Suspense>} />
                  <Route path="FieldMaintenance/:FieldMaintenanceId" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<AddFieldMaintenance />} /></Suspense>} />
                  <Route path="fieldMaintenancepage" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<FieldMaintenancelist />} /></Suspense>} />
                  <Route path="addfieldMaintenancepage" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<AddFieldMaintenanceitem />} /></Suspense>} />
                  <Route path="fieldMaintenancepage/:fieldMaintenanceid" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<AddFieldMaintenanceitem />} /></Suspense>} />
                  <Route path="rentalEquipment" element={<Suspense fallback={<Loader />}><RentalEquipmentRoute element={<RentalEquipment />} /></Suspense>} />
                  <Route path="gradeList" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<GradeList />} /></Suspense>} />
                  <Route path="addGrade" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddGrade />} /></Suspense>} />
                  <Route path="gradeList/:gradeId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddGrade />} /></Suspense>} />
                  <Route path="colorCodeList" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<ColorCodeList />} /></Suspense>} />
                  <Route path="addColorCode" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddColorCode />} /></Suspense>} />
                  <Route path="maintenance" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<Maintenance />} /></Suspense>} />
                  <Route path="maintenance/add" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<ShopMasterDataMaintenance />} /></Suspense>} />
                  <Route path="maintenance/edit" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<ShopMasterDataMaintenance />} /></Suspense>} />
                  <Route path="maintenance/view" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<ShopMasterDataMaintenance />} /></Suspense>} />
                  <Route path="maintenance/history" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<ShopMaintenanceHistory />} /></Suspense>} />
                  <Route path="maintenance/history/details" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<ShopMaintenanceHistoryDetail />} /></Suspense>} />
                  <Route path="maintenance/field/add" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<FieldMasterDataMaintenance />} /></Suspense>} />
                  <Route path="maintenance/field/edit" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<FieldMasterDataMaintenance />} /></Suspense>} />
                  <Route path="maintenance/field/view" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<FieldMasterDataMaintenance />} /></Suspense>} />
                  <Route path="maintenance/field/history" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<FieldMaintenanceHistory />} /></Suspense>} />
                  <Route path="maintenance/field/history/details" element={<Suspense fallback={<Loader />}><MaintenanceRoute element={<FieldMaintenanceHistoryDetails />} /></Suspense>} />
                  <Route path="shopPersonal" element={<Suspense fallback={<Loader />}><ShopPersonalRoute element={<ShopPersonal />} /></Suspense>} />
                  <Route path="receiving" element={<Suspense fallback={<Loader />}><ReceivingRoute element={<Receiving />} /></Suspense>} />
                  <Route path="displayrecieving/:recievingId" element={<Suspense fallback={<Loader />}><ReceivingRoute element={<UpdateRecievingDetails />} /></Suspense>} />
                  <Route path="inventory" element={<Suspense fallback={<Loader />}><InventoryRoute element={<InventoryView />} /></Suspense>} />
                  <Route path="certificateList" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<CertificateList />} /></Suspense>} />
                  <Route path="addCertificate" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddCertificate />} /></Suspense>} />
                  <Route path="certificateList/:certificateId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddCertificate />} /></Suspense>} />
                  <Route path="humanResource" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<HumanResource />} /></Suspense>} />
                  <Route path="addEmployee" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<AddEmployee />} /></Suspense>} />
                  <Route path="addEmployee/:employeeId" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<AddEmployee />} /></Suspense>} />
                  <Route path="employeeContact" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<EmployeeContact />} /></Suspense>} />
                  <Route path="addGroup" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<AddGroup />} /></Suspense>} />
                  <Route path="addGroup/:groupId" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<AddGroup />} /></Suspense>} />
                  <Route path="allGroupList" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<AllGroupList />} /></Suspense>} />
                  <Route path="sendMail/:Id" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<SendMail />} /></Suspense>} />
                  <Route path="templateList" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<TemplateLists />} /></Suspense>} />
                  <Route path="addTemplate" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<AddTemplate />} /></Suspense>} />
                  <Route path="addTemplate/:templateId" element={<Suspense fallback={<Loader />}><HumanResourceRoute element={<AddTemplate />} /></Suspense>} />
                  <Route path="shopDesignation" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<ShopDesignationList />} /></Suspense>} />
                  <Route path="addShopDesignation" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddShopDesignation />} /></Suspense>} />
                  <Route path="title" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<TitleList />} /></Suspense>} />
                  <Route path="addTitle" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddTitle />} /></Suspense>} />
                  <Route path="dropDownPage" element={<Suspense fallback={<Loader />}><DashboardAndProjectRoute element={<ProjectListingPage />} /></Suspense>} />
                  <Route path="equipmentList" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<EquipmentList />} /></Suspense>} />
                  <Route path="addEquipment" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddEquipment />} /></Suspense>} />
                  <Route path="equipmentList/:equipmentId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddEquipment />} /></Suspense>} />
                  <Route path="projectTypeList" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<ProjectTypeList />} /></Suspense>} />
                  <Route path="addProjectType" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddProjectType />} /></Suspense>} />
                  <Route path="projectTypeList/:projectTypeId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddProjectType />} /></Suspense>} />
                  <Route path="generalContractorList" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<GeneralContractorList />} /></Suspense>} />
                  <Route path="addGeneralContractor" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddGeneralContractor />} /></Suspense>} />
                  <Route path="generalContractorList/:generalContractorId" element={<Suspense fallback={<Loader />}><MasterDataRoute element={<AddGeneralContractor />} /></Suspense>} />
                  <Route path="*" element={<PageNotFound />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </AbacProvider>
        </div>
      }
    </div>
  );
}

export default App;
